import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  styled,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Avatar,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ChevronLeft } from '@mui/icons-material';

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  overflow: 'auto',
  maxHeight: '80vh',
  '&::-webkit-scrollbar': {
    width: '0px',
    background: 'transparent', // Optional: just make scrollbar invisible
  },
  scrollbarWidth: 'none' /* Firefox */,
  '-ms-overflow-style': 'none' /* Internet Explorer and Edge */,
  '&::-webkit-scrollbar-thumb': {
    background: 'transparent', // Optional: hide scrollbar thumb
  },
  padding: '0px 15px',
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  position: 'sticky',
  bottom: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
  padding: '10px',
  display: 'flex',
  justifyContent: 'flex-end', // Align buttons to the right
  gap: '10px',
}));

const GenericModal = ({
  open,
  onClose,
  title,
  children,
  primaryButtonText = 'Save',
  secondaryButtonText = 'Cancel',
  onPrimaryAction,
  onSecondaryAction,
  primaryButtonProps,
  secondaryButtonProps,
  maxWidth = 'sm',
  fullWidth = true,
  showCloseIcon = false,
  primaryButtonColor = 'primaryBtn',
  secondaryButtonColor = 'contained',
  primaryButtonVariant = 'contained',
  secondaryButtonVariant = 'outlined',
  isLoading = false,
  isDanger = false,
  isButtonLeft = false,
  onTitleButtonClick,
  desktopWidth = '447px',
  removePrimaryButton = false,
  disabled = false,
  propZindex,
  buttonsInHeader = false,
  hideFooter = false,
  modalIcon,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const handlePrimaryAction = () => {
    if (onPrimaryAction) {
      onPrimaryAction();
    }
  };

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction();
    }
    onClose();
  };

  const dialogStyle = {
    ...(isDesktop && desktopWidth
      ? {
          '& .MuiDialog-paper': {
            width: desktopWidth,
            maxWidth: desktopWidth,
            borderRadius: '1rem',
          },
        }
      : {}),
    zIndex: propZindex,
  };

  // Action buttons component to be reused in header or footer
  const ActionButtons = () => (
    <>
      <Button
        onClick={handleSecondaryAction}
        color={buttonsInHeader ? 'error' : secondaryButtonColor}
        disabled={disabled || secondaryButtonProps?.disabled}
        variant={buttonsInHeader ? 'contained' : secondaryButtonVariant}
        {...secondaryButtonProps}
        sx={{
          minWidth: '120px', // Set a minimum width
          height: '36px', // Set a fixed height
          // ...(buttonsInHeader && {
          //   color: theme.palette.error.main,
          //   borderColor: theme.palette.error.main,
          //   backgroundColor: 'transparent',
          //   '&:hover': {
          //     borderColor: theme.palette.error.dark,
          //     color: theme.palette.error.dark,
          //     backgroundColor: 'rgba(211, 47, 47, 0.08)',
          //   },
          // }),
        }}
      >
        {secondaryButtonText}
      </Button>
      {!removePrimaryButton && (
        <Button
          onClick={handlePrimaryAction}
          color={primaryButtonColor}
          disabled={disabled || isLoading || primaryButtonProps?.disabled}
          variant={primaryButtonVariant}
          {...primaryButtonProps}
          sx={{
            color: '#fff',
            minWidth: '120px', // Set a minimum width
            height: '36px', // Set a fixed height
            '&:hover': {
              backgroundColor: theme.palette.primaryBtn.main, // Use the hover color we defined earlier
              opacity: 0.9, // Optional: reduce opacity on hover for a subtle effect
            },
            '&.Mui-disabled': {
              backgroundColor: '#25865e',
              color: 'white',
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            primaryButtonText
          )}
        </Button>
      )}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      aria-labelledby="modal-title"
      sx={dialogStyle}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id="modal-title"
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {modalIcon ? (
          <Avatar
            sx={{
              bgcolor: 'primary.contrastText',
              color: 'primary.main',
              width: 32,
              height: 32,
            }}
          >
            {modalIcon}
          </Avatar>
        ) : isButtonLeft ? (
          <button
            onClick={onTitleButtonClick}
            style={{
              borderRadius: '100%',
              width: '2.25rem !important',
              height: '2.25rem !important',
              backgroundColor: '#EBEFF6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2px !important',
              margin: '2px !important',
            }}
          >
            <ChevronLeft className="w-[1.5rem] h-[1.5rem] text-mediumBlue" />
          </button>
        ) : null}

        <Box sx={{ flex: 1 }}>
          <h2
            className="font-bold text-base m-0"
            style={{ margin: '0 !important', fontSize: '1.1rem' }}
          >
            {title}
          </h2>
        </Box>

        <div className="flex items-center gap-2">
          {buttonsInHeader && <ActionButtons />}

          {showCloseIcon && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              size="small"
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </div>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2.5 }}>{children}</Box>
      </DialogContent>

      {!buttonsInHeader && !hideFooter && (
        <DialogActions sx={{ px: 2, bgcolor: 'grey.50' }}>
          <Button
            onClick={handleSecondaryAction}
            color="error"
            disabled={disabled || secondaryButtonProps?.disabled}
            variant={secondaryButtonVariant}
            size="medium"
            {...secondaryButtonProps}
            sx={{
              minWidth: 100,
            }}
          >
            {secondaryButtonText}
          </Button>
          {!removePrimaryButton && (
            <Button
              onClick={handlePrimaryAction}
              variant="contained"
              disabled={disabled || isLoading || primaryButtonProps?.disabled}
              size="medium"
              {...primaryButtonProps}
              sx={{
                minWidth: 100,
                px: 3,
                py: 0.75,
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                primaryButtonText
              )}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GenericModal;
